
import { parseIrcLine, parsePrivmsg, ChatMessage, IrcLine } from 'lib/twitchChatParse'
import logger from 'logger'

const log = logger.extend('twitch-chat')

const CHAT_WSS_URL = 'wss://irc-ws.chat.twitch.tv:443'
const JOIN_CHUNK_SIZE = 15
const JOIN_CHUNK_INTERVAL_MS = 11000
const MAX_BACKOFF_MS = 30000
const CLIENT_PING_INTERVAL_MS = 240000
const STALE_MS = 390000
const WATCHDOG_TICK_MS = 60000

export interface ChatHandlers {
  onMessage: (message: ChatMessage) => void
  onMessageDeleted: (e: { channel: string, targetMsgId: string }) => void
  onChatCleared: (e: { channel: string, targetLogin: string | null }) => void
  onRoomId?: (e: { channel: string, roomId: string }) => void
  onConnected?: () => void
}

export interface ChatConnection {
  close: () => void
}

const randomFloat = (): number => {
  const buf = new Uint32Array(1)
  crypto.getRandomValues(buf)
  return buf[0] / 2 ** 32
}

const anonymousNick = (): string =>
  `justinfan${(Math.floor(randomFloat() * 80000) + 10000)}`

const normalizeChannel = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^#/, '')

const isValidChannelLogin = (login: string): boolean =>
  /^[a-z0-9_]{1,25}$/.test(login)

const stripHash = (param: string | undefined): string =>
  (param ?? '').replace(/^#/, '')

export const connectTwitchChat = (channels: string[], handlers: ChatHandlers): ChatConnection => {
  const logins = [...new Set(channels.map(normalizeChannel))].filter(isValidChannelLogin)

  let ws: WebSocket | null = null
  let closed = false
  let attempt = 0
  let lastReceivedAt = 0
  let keepaliveTimers: Array<ReturnType<typeof globalThis.setInterval>> = []
  const timers = new Set<ReturnType<typeof globalThis.setTimeout>>()

  const clearKeepalive = (): void => {
    keepaliveTimers.forEach((t) => globalThis.clearInterval(t))
    keepaliveTimers = []
  }

  const startKeepalive = (socket: WebSocket): void => {
    clearKeepalive()
    lastReceivedAt = Date.now()
    keepaliveTimers = [
      globalThis.setInterval(() => {
        if (ws === socket && socket.readyState === WebSocket.OPEN) socket.send('PING')
      }, CLIENT_PING_INTERVAL_MS),
      globalThis.setInterval(() => {
        if (ws !== socket) return
        const silentForMs = Date.now() - lastReceivedAt
        if (silentForMs > STALE_MS) {
          log({ staleConnection: true, silentForMs })
          socket.close()
        }
      }, WATCHDOG_TICK_MS)
    ]
  }

  const later = (fn: () => void, ms: number): void => {
    const t = globalThis.setTimeout(() => {
      timers.delete(t)
      fn()
    }, ms)
    timers.add(t)
  }

  const joinInChunks = (socket: WebSocket): void => {
    const chunkCount = Math.ceil(logins.length / JOIN_CHUNK_SIZE)
    Array.from({ length: chunkCount }, (_, i) =>
      logins.slice(i * JOIN_CHUNK_SIZE, (i + 1) * JOIN_CHUNK_SIZE)
    ).forEach((chunk, i) => {
      later(() => {
        if (socket.readyState !== WebSocket.OPEN) return
        socket.send(`JOIN ${chunk.map((c) => `#${c}`).join(',')}`)
      }, i * JOIN_CHUNK_INTERVAL_MS)
    })
  }

  const dispatch = (socket: WebSocket, line: IrcLine): void => {
    if (line.command === 'PING') {
      socket.send(`PONG :${line.params[0] ?? 'tmi.twitch.tv'}`)
      return
    }
    if (line.command === '001') {
      attempt = 0
      log({ connected: true, channels: logins.length })
      joinInChunks(socket)
      handlers.onConnected?.()
      return
    }
    if (line.command === 'PRIVMSG') {
      const message = parsePrivmsg(line)
      if (message !== null) handlers.onMessage(message)
      return
    }
    if (line.command === 'CLEARMSG') {
      const channel = stripHash(line.params[0])
      const targetMsgId = line.tags['target-msg-id'] ?? ''
      if (channel !== '' && targetMsgId !== '') handlers.onMessageDeleted({ channel, targetMsgId })
      return
    }
    if (line.command === 'CLEARCHAT') {
      const channel = stripHash(line.params[0])
      if (channel === '') return
      handlers.onChatCleared({ channel, targetLogin: line.params[1] ?? null })
      return
    }
    if (line.command === 'ROOMSTATE') {
      const channel = stripHash(line.params[0])
      const roomId = line.tags['room-id'] ?? ''
      if (channel !== '' && roomId !== '') handlers.onRoomId?.({ channel, roomId })
      return
    }
    if (line.command === 'RECONNECT') {
      log({ serverRequestedReconnect: true })
      socket.close()
    }
  }

  const connect = (): void => {
    if (closed) return
    const socket = new WebSocket(CHAT_WSS_URL)
    ws = socket

    socket.onopen = () => {
      socket.send('CAP REQ :twitch.tv/tags twitch.tv/commands')
      socket.send(`NICK ${anonymousNick()}`)
      startKeepalive(socket)
    }

    socket.onmessage = (e: MessageEvent) => {
      if (typeof e.data !== 'string') return
      lastReceivedAt = Date.now()
      e.data.split('\r\n')
        .filter((raw) => raw !== '')
        .forEach((raw) => {
          const line = parseIrcLine(raw)
          if (line !== null) dispatch(socket, line)
        })
    }

    socket.onclose = () => {
      if (closed || ws !== socket) return
      clearKeepalive()
      attempt += 1
      const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS) + Math.round(randomFloat() * 1000)
      log({ disconnected: true, retryInMs: delay })
      later(connect, delay)
    }

    socket.onerror = () => {}
  }

  if (logins.length > 0) connect()

  return {
    close: () => {
      closed = true
      clearKeepalive()
      timers.forEach((t) => globalThis.clearTimeout(t))
      timers.clear()
      ws?.close()
    }
  }
}
